/**
 * Skills Section Component
 * Migrated to TypeScript with subcomponents
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaCode, FaUsers } from 'react-icons/fa';
import { SiPandas } from 'react-icons/si';
import { skills } from '../../data/skills';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

// Authentic MATLAB Logo Component
const MatlabIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 512 512"
    className={className}
    style={style}
  >
    <path
      fill="currentColor"
      d="M495.855 367.604c-28.98-73.177-53.037-148.231-80.725-221.893c-12.23-31.362-24.198-62.986-40.868-92.33c-6.62-10.535-12.956-22.119-23.814-28.86c-2.739-1.74-5.828-2.479-8.88-2.42c-5.084.096-10.14 2.37-14.025 5.65c-14.571 11.543-23.209 28.334-32.896 43.793c-17.114 28.698-35.156 58.748-63.773 77.396c-13.44 9.485-31.039 10.514-43.995 20.686c-17.72 13.36-29.767 32.31-44.298 48.737c-3.31 3.956-8.436 5.53-12.916 7.67C86.315 243.71 42.945 261.35 0 279.916c36.367 28.132 75.115 53.157 112.208 80.321c10.172-2.018 20.383-6.196 30.877-4.339c16.63 5.207 26.377 21.15 34.006 35.721c15.5 31.765 26.7 65.307 39.253 98.283c20.988-1.493 39.878-12.31 56.104-25.025c31.24-25.307 55.034-58.001 81.23-88.192c12.552-13.199 25.347-28.92 44.197-32.593c19.05-4.601 39.454 2.22 53.581 15.338c19.818 17.719 37.376 38.203 60.544 51.765c-3.108-15.338-10.575-29.101-16.145-43.592M171.844 316.04c-18.284 10.817-37.699 19.717-56.71 29.162c-29.828-19.98-58.889-41.15-88.192-61.856c39.515-17.315 79.635-33.4 119.675-49.545c18.97 14.813 38.547 28.88 57.517 43.693c-8.92 14.248-19.657 27.487-32.29 38.546m40.665-49.646c-18.85-14.167-37.94-27.951-56.508-42.482c11.907-16.226 24.945-31.906 40.565-44.701c10.333-6.62 22.966-8.456 33.703-14.43c25.469-12.714 42.845-36.044 59.434-58.324c-21.271 55.216-43.975 110.654-77.194 159.937"
    />
  </svg>
);

const Skills: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('technical');
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const tabs = [
    { id: 'technical', label: 'Technical Skills', icon: FaCode },
    { id: 'soft', label: 'Soft Skills', icon: FaUsers }
  ];

  const getSkillIcon = (skillName: string) => {
    const iconMap: Record<string, any> = {
      'Pandas': SiPandas,
      'MATLAB': MatlabIcon
    };
    return iconMap[skillName] || null;
  };

  const getSkillColor = (skillName: string) => {
    const colorMap: Record<string, string> = {
      'C/C++': '#00599C',
      'C#': '#239120',
      'Xamarin': '#3498DB',
      'XAML': '#0C54C2',
      'Python': '#3776AB',
      'Machine Learning': '#FF6B6B',
      'Deep Learning': '#4ECDC4',
      'NumPy': '#4D77CF',
      'Pandas': '#150458',
      'MATLAB': '#E16737',
      'Matplotlib': '#11557C',
      'Scikit-learn': '#F7931E',
      'PyTorch': '#EE4C2C',
      'Jupyter': '#F37626',
      'Hadoop': '#FF6600',
      'Spark': '#E25A1C',
      'C': '#A8B9CC',
      'Ubuntu': '#E95420',
      'Wireshark': '#1679A7',
      'HTML': '#E34F26',
      'CSS': '#1572B6',
      'JavaScript': '#F7DF1E',
      'PHP': '#777BB4',
      'SQL': '#336791',
      'NoSQL': '#47A248',
      'React': '#61DAFB',
      'Node.js': '#339933',
      'Express': '#000000',
      'MongoDB': '#47A248',
      'MySQL': '#4479A1',
      'PostgreSQL': '#336791',
      'Docker': '#2496ED',
      'Kubernetes': '#326CE5',
      'AWS': '#FF9900',
      'Azure': '#0078D4',
      'Git': '#F05032',
      'GitHub': '#181717',
      'Leadership': '#8E44AD',
      'Communication': '#E74C3C',
      'Problem Solving': '#F39C12',
      'Teamwork': '#27AE60',
      'Adaptability': '#9B59B6',
      'Time Management': '#34495E',
      // ADD MISSING COLORS:
      'Robot Framework': '#000000',
      'Java': '#007396',
      'Maven': '#C71A36',
      'Appium': '#41BDF5',
      'Android Studio': '#3DDC84',
      'Azure DevOps': '#0078D7',
      'Jenkins': '#D24939',
      'MQTT': '#660066',
      'HTML/CSS': '#E34F26',
      'REST API': '#009688',
      'Data Structures & Algorithms': '#4A90E2',
      'Operating Systems': '#5C5C5C',
      'PySpark': '#E25A1C',
      'Cybersecurity': '#FF0000',
      'Network Security': '#FF6600',
      'Web Security': '#DC3545',
      'Cryptography': '#FFD700',
      'ASP.NET': '#512BD4',
      'JSON': '#000000',
      'PowerShell': '#012456',
      'Bash': '#4EAA25',
      'Z shell': '#00BC87',
      'Ansible': '#EE0000',
      'Selenium': '#43B02A',
      'GDB Debugger': '#DA4437',
      'Postman': '#FF6C37',
      'OpenCL': '#0071C5',
      'Jira': '#0052CC',
      'DynamoDB': '#3F48CC',
      'Amazon S3': '#569A31',
      'Power BI': '#F2C811',
      'Windows': '#0078D4',
      'macOS': '#000000',
      'Linux': '#FCC624',
      'Kali': '#557C94',
      'CentOS': '#262577',
      'OpenSUSE': '#73BA25',
      'Arch Linux': '#1793D1',
      'Pintos': '#6C5CE7',
      'Critical Thinking': '#3498DB',
      'Creativity': '#E91E63'
    };
    return colorMap[skillName] || '#6C757D';
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" ref={sectionRef} className="py-12">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                Skills
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Technical expertise and professional competencies
            </p>
          </div>

          {/* Skills Tabs */}
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-2xl p-1 shadow-inner">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                    activeTab === tab.id
                      ? `${colors.bg} text-white shadow-lg transform scale-105`
                      : `${colors.textSecondary} hover:${colors.hoverAccent} hover:scale-105`
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

                  {/* Skills Content */}
                  {activeTab === 'technical' ? (
                    <div className="space-y-6">
                      {Object.entries(skills.technical).map(([categoryName, categorySkills]) => (
                        <div key={categoryName}>
                          <h3 className="text-xl font-semibold mb-3 flex items-center">
                            <span className="accent-gradient-text">{categoryName}</span>
                            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                              ({categorySkills.length})
                            </span>
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                            {categorySkills.map((skill: any, index: number) => {
                              const CustomIcon = getSkillIcon(skill.name);
                              return (
                                <div
                                  key={skill.name}
                                  className={`${colors.cardBackground} rounded-xl p-3 shadow-lg border ${colors.borderColor} hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-3`}
                                  style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                  {CustomIcon ? (
                                    <CustomIcon 
                                      className="w-6 h-6 flex-shrink-0 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                                      style={{ color: getSkillColor(skill.name) }}
                                    />
                                  ) : (
                                    <skill.icon 
                                      className="w-6 h-6 flex-shrink-0 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                                      style={{ color: getSkillColor(skill.name) }}
                                    />
                                  )}
                                  <span className={`text-sm font-semibold ${colors.textPrimary} leading-tight`}>
                                    {skill.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                      {skills.soft.map((skill: any, index: number) => {
                        const CustomIcon = getSkillIcon(skill.name);
                        return (
                          <div
                            key={skill.name}
                            className={`${colors.cardBackground} rounded-xl p-3 shadow-lg border ${colors.borderColor} hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-3`}
                            style={{ animationDelay: `${index * 0.05}s` }}
                          >
                            {CustomIcon ? (
                              <CustomIcon 
                                className="w-6 h-6 flex-shrink-0 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                                style={{ color: getSkillColor(skill.name) }}
                              />
                            ) : (
                              <skill.icon 
                                className="w-6 h-6 flex-shrink-0 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                                style={{ color: getSkillColor(skill.name) }}
                              />
                            )}
                            <span className={`text-sm font-semibold ${colors.textPrimary} leading-tight`}>
                              {skill.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
        </div>
      </div>
    </section>
  );
};

export default Skills;

